import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PieChart from "../components/PieChart";
import Loading from "../components/Loading";
import "./Dashboard.css";
import { apiFetch } from "../components/api"

export default function DashboardPage() {
  // ────────────── 대분류 파이차트 ──────────────
  const [catData, setCatData] = useState([]);
  const [pcbValues, setPcbValues] = useState([1]);
  const [pcbLabels, setPcbLabels] = useState(["No Data"]);
  const [catLabels, setCatLabels] = useState([]);
  const pieColors = [
    "#4F46E5", // 인디고
    "#22C55E", // 그린
    "#0EA5E9", // 시안
    "#F97316", // 오렌지
    "#EC4899", // 핑크
    "#6366F1", // 퍼플블루
    "#14B8A6", // 틸
    "#64748B", // 슬레이트
  ];
  const pcbPieColors = [
    "#0F766E",
    "#0891B2",
    "#2563EB",
    "#7C3AED",
    "#DB2777",
    "#EA580C",
    "#CA8A04",
    "#6B7280",
  ];

  const navigate = useNavigate();

  useEffect(() => {
    fetch("/api/dashboard/category-distribution/")
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        const counts = data.data?.map((it) => it.product_count) || [];
        const names = data.data?.map((it) => it.name) || [];
        setCatData(counts.length ? counts : [1]);
        setCatLabels(names.length ? names : ["No Data"]);
      })
      .catch((e) => console.error("category-distribution error", e));
  }, []);

  useEffect(() => {
  fetch("/api/dashboard/pcb-models/?include_zero=1")
    .then(r => r.json())
    .then(json => {
      const rows = Array.isArray(json.data) ? json.data : [];
      const values = rows.map(it => it.qty);
      const labels = rows.map(it => it.model || "(이름 없음)");
      const allZero = values.length > 0 && values.every(v => v === 0);

      if (rows.length === 0 || allZero) {
        setPcbValues([1]);
        setPcbLabels(["No PCB Stock"]);
      } else {
        setPcbValues(values);
        setPcbLabels(labels);
      }
    })
    .catch(e => console.error("pcb-models error", e));
  }, []);

  // ────────────── 재고 부족 경보 ──────────────
  const [lowStock, setLowStock] = useState([]);
  const [lowStockLoading, setLowStockLoading] = useState(false);
  const [lte, setLte] = useState(5);

  const loadLowStock = () => {
    setLowStockLoading(true);
    fetch(`/api/dashboard/low-stock/?lte=${lte}&limit=20`, { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => setLowStock(Array.isArray(data.items) ? data.items : []))
      .catch((e) => console.error("low-stock error", e))
      .finally(() => setLowStockLoading(false));
  };

  useEffect(() => {
    loadLowStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ────────────── 월간 입출고 트렌드 ──────────────
  const [flow, setFlow] = useState([]);
  const [months, setMonths] = useState(6);
  const [flowLoading, setFlowLoading] = useState(false);

  const loadFlow = () => {
    setFlowLoading(true);
    fetch(`/api/dashboard/monthly-flow/?months=${months}`, { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => setFlow(Array.isArray(data.series) ? data.series : []))
      .catch((e) => console.error("monthly-flow error", e))
      .finally(() => setFlowLoading(false));
  };

  useEffect(() => {
    loadFlow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ────────────── 최근 입출고 이벤트 ──────────────
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  const loadEvents = async () => {
    setEventsLoading(true);
    try {
      const data = await apiFetch("/api/inventory/logs/?limit=20", {
        method: "GET",
        cache: "no-store",
      });
      setEvents(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      const msg = String(e?.message || "알 수 없는 오류");
      if (/^(세션|로그인)/.test(msg)) {
        alert(msg);
      } else {
        alert(`입출고 이벤트 로드 에러 : ${msg}`);
      }
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  // ────────────── 간단 SVG 바차트 (외부 라이브러리 없이) ──────────────
  const FlowBarChart = ({ data }) => {
    const maxVal = useMemo(() => {
      const all = data.flatMap((d) => [d.in_qty || 0, d.out_qty || 0]);
      return Math.max(1, ...all);
    }, [data]);

    const width = 640;
    const barWidth = 22;
    const gap = 18;
    const pairGap = 28; // IN/OUT 묶음 간
    const height = 220;
    const padding = 32;
    const perVal = (val) => ((height - padding * 2) * val) / maxVal;

    return (
      <svg className="mini-svg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="월간 입출고 차트">
        {/* 축 */}
        <line x1="40" y1={height - padding} x2={width - padding} y2={height - padding} stroke="#999" strokeWidth="1" />
        <line x1="40" y1={padding} x2="40" y2={height - padding} stroke="#999" strokeWidth="1" />

        {data.map((d, idx) => {
          const x0 = 60 + idx * (barWidth * 2 + gap + pairGap);
          const inH = perVal(d.in_qty || 0);
          const outH = perVal(d.out_qty || 0);
          const baseY = height - padding;
          return (
            <g key={d.month}>
              {/* IN 바 */}
              <rect
                x={x0}
                y={baseY - inH}
                width={barWidth}
                height={inH}
                className="bar-in"
              />
              {/* OUT 바 */}
              <rect
                x={x0 + barWidth + gap}
                y={baseY - outH}
                width={barWidth}
                height={outH}
                className="bar-out"
              />
              {/* 라벨 */}
              <text x={x0 + barWidth / 2} y={height - 8} className="tick" textAnchor="middle">
                {d.month.slice(5, 7)}월
              </text>
            </g>
          );
        })}
        {/* 범례 */}
        <g>
          <rect x={width - 160} y={14} width={12} height={12} className="bar-in" />
          <text x={width - 142} y={24} className="legend">IN</text>
          <rect x={width - 100} y={14} width={12} height={12} className="bar-out" />
          <text x={width - 82} y={24} className="legend">OUT</text>
        </g>
      </svg>
    );
  };

  // ────────────── 렌더 ──────────────
  return (
    <div className="dashboard-page">
      <Loading active={lowStockLoading || flowLoading || eventsLoading} />
      <div className="dash-wrap scroll-y-custom">
        <header className="dash-header">
          <div>
            <h1 className="dash-title">📊 통합 현황</h1>
          </div>
        </header>

        <main className="dash-main">
          {/* 대분류 파이차트 */}
          <section className="card">
            <div className="card-head">
              <h2 className="card-title">대분류 현황</h2>
              <h2 className="card-title" style={{ flex: '0 0 50%', textAlign: 'left' }}>PCB 현황</h2>
            </div>
            <div className="card-body">
              <div className="hstack-2">
                {catData.length > 0 ? (
                  <PieChart data={catData} colors={pieColors} labels={catLabels} />
                ) : <p className="muted">Loading…</p>}

                {pcbValues.length > 0 ? (
                  <PieChart data={pcbValues} colors={pcbPieColors} labels={pcbLabels} />
                ) : <p className="muted">Loading…</p>}
              </div>
            </div>
          </section>

          {/* 월간 입출고 트렌드 */}
          <section className="card">
            <div className="card-head">
              <h2 className="card-title">월간 입출고 트렌드</h2>
              <div className="inline-controls">
                <label className="small">개월</label>
                <input
                  className="small-input"
                  type="number"
                  min={1}
                  max={24}
                  value={months}
                  onChange={(e) => setMonths(Number(e.target.value || 1))}
                />
                <button className="dash-btn small" onClick={loadFlow} disabled={flowLoading}>
                  적용
                </button>
              </div>
            </div>
            <div className="card-body center">
              {flowLoading ? (
                <p className="muted">Loading…</p>
              ) : flow.length ? (
                <FlowBarChart data={flow} />
              ) : (
                <p className="muted">데이터 없음</p>
              )}
            </div>
          </section>

          {/* 재고 부족 경보 */}
          <section className="card bottom">
            <div className="card-head">
              <h2 className="card-title">재고 부족 경보</h2>
              <div className="inline-controls">
                <label className="small">임계값 ≤</label>
                <input
                  className="small-input"
                  type="number"
                  min={0}
                  value={lte}
                  onChange={(e) => setLte(Number(e.target.value || 0))}
                />
                <button className="dash-btn small" onClick={loadLowStock} disabled={lowStockLoading}>
                  적용
                </button>
              </div>
            </div>
            <div className="card-body">
              {lowStockLoading ? (
                <p className="muted">Loading…</p>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>제품</th>
                        <th>대분류</th>
                        <th>카테고리</th>
                        <th>패키지</th>
                        <th>재고</th>
                        <th>위치</th>
                        <th>상태</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lowStock.length === 0 && (
                        <tr><td colSpan={7} className="muted center">부족 품목 없음</td></tr>
                      )}
                      {lowStock.map((it) => (
                        <tr key={it.id}>
                          <td className="ellipsis" title={it.product_name}>{it.product_name}</td>
                          <td>{it.major_category || "-"}</td>
                          <td>{it.category || "-"}</td>
                          <td>{it.package || "-"}</td>
                          <td className={Number(it.stock) <= 0 ? "danger" : ""}>{it.stock}</td>
                          <td className="ellipsis" title={it.location}>{it.location || "-"}</td>
                          <td>{it.state || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>

          <section className="card bottom">
            <div className="card-head">
              <h2 className="card-title">최근 입출고 이벤트</h2>
              <div className="inline-controls">
                <button className="dash-btn small" onClick={() => navigate("/logs")}>
                  전체 이력
                </button>
                <button className="dash-btn small" onClick={loadEvents} disabled={eventsLoading}>
                  {eventsLoading ? "새로고침…" : "새로고침"}
                </button>
              </div>
            </div>
            <div className="card-body">
              {eventsLoading ? (
                <p className="muted">Loading…</p>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{width: "180px"}}>시간</th>
                        <th>제품</th>
                        <th style={{width: "140px"}}>변경사항</th>
                        <th style={{width: "120px"}}>최종재고</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.length === 0 && (
                        <tr><td colSpan={4} className="muted center">최근 이벤트 없음</td></tr>
                      )}
                      {events.map((ev) => {
                        const ts = new Date(ev.timestamp).toLocaleString();
                        const delta = Number(ev.quantity_change);
                        const signed = delta > 0 ? `+${delta}` : `${delta}`;
                        return (
                          <tr key={ev.id}>
                            <td className="nowrap">{ts}</td>
                            <td className="ellipsis" title={ev.product_name || ""}>{ev.product_name || "-"}</td>
                            <td className={`right ${delta < 0 ? "danger" : "good"}`}>{signed}</td>
                            <td className="right">{ev.final_stock}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
